import http from '../../shared/http.js';
import { initLiveList } from '../../shared/liveList.js';
import { initFormModalLinks, formModalSaved, isFormModalOpen } from '../../shared/formModal.js';
import { bindFormModule } from '../../shared/formBinding.js';
import {
  confirmAction,
  hideLoading,
  notifyError,
  notifySuccess,
  showLoading
} from '../../shared/notify.js';
import { withFlash } from '../../shared/flash.js';

// Every scheduled job in one table (E01).
//
// Two things happen here beyond the live list: the switch in each row, and the
// schedule form in the shared modal. Neither touches a timer - both write the
// row, and the supervisor in utils/scheduler.js moves the timers within the
// minute. That is also why the row is refetched after a save rather than
// patched in place: the server is the one that decides what the row now says.

// The live list's handle, set by initZeitplanUebersicht below. Module state
// because the switch and the form both have to refetch the table after a write,
// and neither is called from where it was created.
let liste = null;

// The five cron shapes the server's zeitplanText() reads, mirrored so the form
// can translate while somebody is typing. Deliberately the same small set: a
// shape neither side recognises is shown as itself rather than guessed at.
function lesbar(ausdruck) {
  const felder = String(ausdruck || '')
    .trim()
    .split(/\s+/);
  if (felder.length !== 5) return '';

  const [minute, stunde, tag, monat, wochentag] = felder;
  const jederTag = tag === '*' && monat === '*' && wochentag === '*';
  const zahl = (wert) => String(wert).padStart(2, '0');

  if (jederTag && stunde === '*' && /^\d+$/.test(minute)) return `stündlich :${zahl(minute)}`;
  if (jederTag && /^\d+$/.test(stunde) && /^\d+$/.test(minute)) {
    return `täglich ${zahl(stunde)}:${zahl(minute)}`;
  }

  return '';
}

async function schalte(eingabe) {
  const { schluessel } = eingabe.dataset;
  const aktiv = eingabe.checked;

  // Disabled while the request is in flight: a second click would race the
  // first, and the row is about to be replaced anyway.
  eingabe.disabled = true;

  try {
    await http.patch(`/zeitplan/${schluessel}`, { aktiv });
    notifySuccess(aktiv ? 'Job wurde eingeschaltet.' : 'Job wurde ausgeschaltet.');
    // The server decides what the row says now, including the "gespeichert"
    // mark this very click may have just earned.
    liste?.refresh();
  } catch (err) {
    // Put the switch back where it was: the change did not happen, and a
    // toggle left in the new position would claim it did.
    eingabe.checked = !aktiv;
    eingabe.disabled = false;
    notifyError(err, 'Der Schalter konnte nicht geändert werden.');
  }
}

// Starting a job by hand.
//
// Confirmed first, every job, not only Salesforce: each of these talks to an
// external system or writes across the whole database, none is undoable from
// here, and a row of buttons next to a filter is easy to hit by accident. The
// Salesforce one says more, because that run pushes to a live external system
// and reversing it is not a thing this application can offer.
async function starte(knopf) {
  const { schluessel, name } = knopf.dataset;

  const salesforce = schluessel === 'salesforce';
  const bestaetigt = await confirmAction({
    title: `${name} jetzt starten?`,
    message: salesforce
      ? 'Der Abgleich überträgt Änderungen an Salesforce. Was gesendet wurde, lässt sich hier nicht zurücknehmen.'
      : 'Der Job läuft sofort los, zusätzlich zu seinem Zeitplan.',
    okText: 'Jetzt starten'
  });

  if (!bestaetigt) return;

  knopf.disabled = true;

  try {
    const antwort = await http.post(`/zeitplan/${schluessel}/start`);

    // 202, not 200: the run was started, not finished. The message has to say
    // so, or an empty log a second later reads as a failure. axios puts the
    // body on .data, and the body's own envelope nests the payload under .data
    // again - hence both.
    notifySuccess(antwort?.data?.data?.hinweis || 'Der Lauf wurde gestartet.');
  } catch (err) {
    notifyError(err, 'Der Lauf konnte nicht gestartet werden.');
  } finally {
    // Re-enabled either way: the row is not refetched here, because the run is
    // still going and the table would show the same "last run" as before.
    knopf.disabled = false;
  }
}

function readForm(form) {
  const ausdruck = form.querySelector('#zeitplan-ausdruck')?.value.trim() || '';

  return {
    ausdruck,
    // An unchecked checkbox is not submitted at all, so the absent key is the
    // "false" the server needs to see - FormData cannot express it.
    aktiv: form.querySelector('#zeitplan-aktiv')?.checked ?? true
  };
}

async function submit(form, werte) {
  const submitButton = form.querySelector('button[type="submit"]');
  const { schluessel } = form.dataset;

  if (submitButton) submitButton.disabled = true;
  showLoading('Wird gespeichert …');

  try {
    await http.patch(`/zeitplan/${schluessel}`, werte || readForm(form));

    if (isFormModalOpen()) {
      hideLoading();
      // Before formModalSaved, for the reason kundeForm.js spells out: onSaved
      // may navigate, and Notiflix painting into an unloading document throws.
      notifySuccess('Zeitplan wurde gespeichert. Er wird innerhalb einer Minute übernommen.');
      formModalSaved();
    } else {
      window.location.href = withFlash('/zeitplan', 'zeitplan-gespeichert');
    }
  } catch (err) {
    hideLoading();
    if (submitButton) submitButton.disabled = false;
    notifyError(err, 'Der Zeitplan konnte nicht gespeichert werden.');
  }
}

// The live translation under the field, and the reset. Both have to run again
// for every form the modal delivers, which is what the setup argument is for.
function setupForm(form) {
  const feld = form.querySelector('#zeitplan-ausdruck');
  const anzeige = form.querySelector('#zeitplan-lesbar');

  if (feld && anzeige) {
    feld.addEventListener('input', () => {
      anzeige.textContent = lesbar(feld.value);
    });
  }

  // Clearing both fields is what hands the job back to the environment
  // variable. Sent explicitly as null rather than by deleting the row, so the
  // entry keeps saying who last changed it.
  form.querySelector('[data-action="zuruecksetzen"]')?.addEventListener('click', () => {
    submit(form, { ausdruck: null, aktiv: null });
  });
}

export function initZeitplanUebersicht() {
  const region = document.getElementById('zeitplan-list');
  if (!region) return;

  // No polling. A job that runs hourly has nothing new to say between two
  // visits, and a table that refreshed itself would leave a reader wondering
  // whether something had changed.
  liste = initLiveList({
    results: 'zeitplan-list',
    path: '/zeitplan',
    forms: ['zeitplan-filter']
  });

  // Delegated from the results region, so the switches inside a fragment that
  // has just been swapped in are live without rebinding anything.
  region.addEventListener('change', (event) => {
    const eingabe = event.target.closest('[data-action="schalter"]');
    if (eingabe) schalte(eingabe);
  });

  // Delegated for the same reason as the switch above: the buttons live inside
  // the fragment that liveList.js swaps out on every filter change.
  region.addEventListener('click', (event) => {
    const knopf = event.target.closest('[data-action="starten"]');
    if (knopf) starte(knopf);
  });

  initFormModalLinks({
    selector: 'a[href^="/zeitplan/"][href$="/edit"]',
    onSaved: () => liste?.refresh()
  });

  bindFormModule('zeitplan-form', submit, setupForm);
}
